import { groupBy, intersection, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { getProductionGroupName } from '@paragon/production-app-utilities';

import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import { getAggregatedTrussStatisticsByBundleGuid, getBundlesByTruckloadGuid } from './bundles';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { getProductionComponentInstancesByTruckloadGuid } from './getProductionComponentInstancesByTruckloadGuid';
import { getStatusesByTruckloadGuid } from './getStatusesByTruckloadGuid';
import { getStatusPercentages, StatusPercentage } from './getStatusPercentages';
import { getGroupedProductionComponentInstancesByBundleGuid } from './productionComponentInstances';
import {
  getAggregatedTrussStatisticsByProductionGroupGuid,
  getProductionGroupsByProjectGuid,
} from './productionGroups';
import { getAggregatedTrussStatisticsByTruckloadGuid } from './truckloads';

interface BasicRowInformation {
  readonly type: 'truckload' | 'productionGroup' | 'bundle' | 'groupedProductionComponentInstance';
  readonly rowId: string;
  readonly guid: string;
  readonly name: string;
  readonly statusPercentages: readonly StatusPercentage[];
}

interface TruckloadTruckloadGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'truckload';
  readonly trailer: string | null;
  readonly truckDriver: string | null;
  readonly shipDate: string | null;
}

export interface ProductionGroupTruckloadGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'productionGroup';
  readonly truckloadRowId: string;
}

export interface BundleTruckloadGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'bundle';
  readonly productionGroupRowId: string;
}

export interface GroupedProductionComponentInstanceTruckloadGridRow
  extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'groupedProductionComponentInstance';
  readonly bundleGuid: string;
  readonly analysisSetGuid: string;
}

export type TruckloadGridRow =
  | ProductionGroupTruckloadGridRow
  | TruckloadTruckloadGridRow
  | BundleTruckloadGridRow
  | GroupedProductionComponentInstanceTruckloadGridRow;

const delineator = '.';

const truckloadRowPrefix = `truckload${delineator}`;
export function getTruckloadRowId(truckloadGuid: string) {
  return `${truckloadRowPrefix}${truckloadGuid}`;
}
export function isTruckloadRow(rowId: string) {
  return rowId.startsWith(truckloadRowPrefix);
}
export function getTruckloadGuidFromRowId(rowId: string) {
  if (!isTruckloadRow(rowId)) throw new Error('Tried to get the truckload GUID of a non-truckload row');
  return rowId.substring(truckloadRowPrefix.length);
}

const productionGroupRowPrefix = `productionGroup${delineator}`;
function getProductionGroupRowId(productionGroupGuid: string, truckloadGuid: string) {
  return `${productionGroupRowPrefix}${productionGroupGuid}${delineator}${truckloadGuid}`;
}
export function isProductionGroupRow(rowId: string) {
  return rowId.startsWith(productionGroupRowPrefix);
}
export function getProductionGroupGuidFromRowId(rowId: string) {
  if (!isProductionGroupRow(rowId))
    throw new Error('Tried to get the production group GUID of a non-production-group row');
  return rowId.split(delineator)[1];
}

const bundleRowPrefix = `bundle${delineator}`;
export function getBundleRowId(bundleGuid: string) {
  return `${bundleRowPrefix}${bundleGuid}`;
}
export function isBundleRow(rowId: string) {
  return rowId.startsWith(bundleRowPrefix);
}
export function getBundleGuidFromRowId(rowId: string) {
  if (!isBundleRow(rowId)) throw new Error('Tried to get the bundle GUID of a non-bundle row');
  return rowId.substring(bundleRowPrefix.length);
}

const groupedProductionComponentInstanceRowPrefix = 'groupedProductionComponentInstance.';
export function getGroupedProductionComponentInstanceRowId(bundleGuid: string, analysisSetGuid: string) {
  return `${groupedProductionComponentInstanceRowPrefix}${bundleGuid}${delineator}${analysisSetGuid}`;
}
export function isGroupedProductionComponentInstanceRow(rowId: string) {
  return rowId.startsWith(groupedProductionComponentInstanceRowPrefix);
}

export const getTruckloadsForTruckloadGrid = createSelector(
  (state: RootState) => state.truckloads,
  getStatusesByTruckloadGuid,
  (state: RootState) => state.truckloadGrid.projectGuidFilter,
  getProductionComponentInstancesByTruckloadGuid,
  getProductionGroupsByProjectGuid,

  (
    truckloads,
    statusesByTruckloadGuid,
    projectGuidFilter,
    productionComponentInstancesByTruckloadGuid,
    productionGroupsByProjectGuid,
  ) =>
    Object.values(truckloads).filter((truckload) => {
      // If a project filter is set, return all truckloads for that project, regardless of status
      if (projectGuidFilter !== null) {
        const instancesForTruckload = productionComponentInstancesByTruckloadGuid[truckload.guid];
        const productionGroupGuids = uniq(instancesForTruckload.map((instance) => instance.productionGroupGuid));
        return (
          intersection(
            productionGroupsByProjectGuid[projectGuidFilter].map((productionGroup) => productionGroup.guid),
            productionGroupGuids,
          ).length > 0
        );
      }

      // If no project filter is set, return truckloads where any component is not delivered
      const statuses = statusesByTruckloadGuid[truckload.guid];
      const hasStatusOtherThanDelivered = statuses.some((status) => status !== 'Delivered');
      return statuses.length === 0 || hasStatusOtherThanDelivered;
    }),
);

export const getTruckloadGridRows = createSelector(
  getTruckloadsForTruckloadGrid,
  getProductionGroupsByProjectGuid,
  (state: RootState) => state.productionGroups,
  getBundlesByTruckloadGuid,
  getAggregatedTrussStatisticsByBundleGuid,
  getAggregatedTrussStatisticsByProductionGroupGuid,
  getAggregatedTrussStatisticsByTruckloadGuid,
  getGroupedProductionComponentInstancesByBundleGuid,
  (state: RootState) => state.trailers,
  (state: RootState) => state.truckDrivers,
  getProductionComponentInstancesByBundleGuid,
  getProductionComponentInstancesByTruckloadGuid,

  (
    truckloads,
    productionGroupsByProjectGuid,
    productionGroups,
    bundlesByTruckloadGuid,
    aggregatedTrussStatisticsByBundleGuid,
    aggregatedTrussStatisticsByProductionGroupGuid,
    aggregatedTrussStatisticsByTruckloadGuid,
    groupedProductionComponentInstancesByBundleGuid,
    trailers,
    truckDrivers,
    productionComponentInstancesByBundleGuid,
    productionComponentInstancesByTruckloadGuid,
  ): readonly TruckloadGridRow[] => {
    const truckloadGridRows: TruckloadGridRow[] = [];

    const truckloadTruckloadGridRowsForProject: TruckloadGridRow[] = [];
    for (const truckload of truckloads) {
      const truckloadRowId = getTruckloadRowId(truckload.guid);

      const bundlesForTruckload = bundlesByTruckloadGuid[truckload.guid];
      const bundlesForTruckloadByProductionGroupGuid = groupBy(
        bundlesForTruckload,
        (bundle) => bundle.productionGroupGuid,
      );
      const productionGroupRowsForTruckload: ProductionGroupTruckloadGridRow[] = [];
      for (const [productionGroupGuid, bundlesForProductionGroupGuidInTruckload] of Object.entries(
        bundlesForTruckloadByProductionGroupGuid,
      )) {
        const productionGroupRowId = getProductionGroupRowId(productionGroupGuid, truckload.guid);
        const bundleRowsForProductionGroupGuidInTruckload: readonly BundleTruckloadGridRow[] =
          bundlesForProductionGroupGuidInTruckload.map((bundle) => ({
            type: 'bundle',
            rowId: getBundleRowId(bundle.guid),
            guid: bundle.guid,
            name: `Bundle ${bundle.id}`,
            statusPercentages: getStatusPercentages(productionComponentInstancesByBundleGuid[bundle.guid]),
            truckloadGuid: bundle.truckloadGuid!,
            ...aggregatedTrussStatisticsByBundleGuid[bundle.guid],
            productionGroupRowId,
          }));
        truckloadGridRows.push(...bundleRowsForProductionGroupGuidInTruckload);

        const productionGroupRowForTruckload: ProductionGroupTruckloadGridRow = {
          type: 'productionGroup',
          rowId: getProductionGroupRowId(productionGroupGuid, truckload.guid),
          guid: productionGroupGuid,
          name: getProductionGroupName(productionGroups[productionGroupGuid], true),
          statusPercentages: getStatusPercentages(
            bundleRowsForProductionGroupGuidInTruckload
              .map((bundleRow) => getBundleGuidFromRowId(bundleRow.rowId))
              .flatMap((bundleGuid) => productionComponentInstancesByBundleGuid[bundleGuid]),
          ),
          ...aggregatedTrussStatisticsByProductionGroupGuid[productionGroupGuid],
          truckloadRowId,
        };
        productionGroupRowsForTruckload.push(productionGroupRowForTruckload);
        truckloadGridRows.push(productionGroupRowForTruckload);
      }

      const truckloadRow: TruckloadGridRow = {
        type: 'truckload',
        rowId: truckloadRowId,
        guid: truckload.guid,
        name: `Truckload ${truckload.id.toString()}`,
        statusPercentages: getStatusPercentages(productionComponentInstancesByTruckloadGuid[truckload.guid]),
        trailer: truckload.trailerGuid ? (trailers[truckload.trailerGuid]?.name ?? null) : null,
        truckDriver: truckload.truckDriverGuid ? (truckDrivers[truckload.truckDriverGuid]?.name ?? null) : null,
        shipDate: truckload.dateShipped,
        ...aggregatedTrussStatisticsByTruckloadGuid[truckload.guid],
      };
      truckloadTruckloadGridRowsForProject.push(truckloadRow);
      truckloadGridRows.push(truckloadRow);

      const groupedProductionComponentInstanceRows: readonly GroupedProductionComponentInstanceTruckloadGridRow[] =
        bundlesForTruckload.flatMap((bundle) =>
          groupedProductionComponentInstancesByBundleGuid[bundle.guid].map(
            ({
              analysisSetGuid,
              guid,
              trussCount,
              trussName,
              instances,
              boardFeet,
              linearFeet,
              heights,
              widths,
              thicknesses,
            }) => ({
              type: 'groupedProductionComponentInstance',
              rowId: getGroupedProductionComponentInstanceRowId(bundle.guid, analysisSetGuid),
              guid,
              name: trussName,
              statusPercentages: getStatusPercentages(instances),
              trussCount,
              boardFeet,
              linearFeet,
              heights,
              widths,
              thicknesses,
              bundleGuid: bundle.guid,
              analysisSetGuid,
            }),
          ),
        );
      truckloadGridRows.push(...groupedProductionComponentInstanceRows);
    }

    return truckloadGridRows;
  },
);

export const getTruckloadGridCheckedBundleGuids = createSelector(
  (state: RootState) => state.truckloadGrid.checkedRows,

  (checkedRows) => checkedRows.filter(isBundleRow).map(getBundleGuidFromRowId),
);
